import { useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import type { Warranty } from '@econtractor/types';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import { ProductsSelect, VendorSelect } from 'modules/product';
import { ObjectSelected } from 'modules/objects';
import useWarrantyObjectOptions from '../model/useWarrantyObjectOptions';
import { addWarrantyMonths, type WarrantyFormValues } from '../model/warrantyDates';
import WarrantyTermMonths from './WarrantyTermMonths';
import WarrantySerialNumbers, { type WarrantySerialNumbersHandle } from './WarrantySerialNumbers';

interface TabGeneralProps {
    row: Warranty | null;
    disabled: boolean;
}

const TabGeneral = ({ row, disabled }: TabGeneralProps) => {
    const { t } = useTranslation('Warranties');
    const { values, setFieldValue, handleChange } = useFormikContext<WarrantyFormValues>();
    const objectSelect = useWarrantyObjectOptions(values.productId, values.objectId, row);
    const serialsRef = useRef<WarrantySerialNumbersHandle>(null);
    const creating = !row?.ID;

    const applyMonths = (months: number) => {
        if (!values.purchasedAt || !Number.isInteger(months) || months < 1) return;
        const until = addWarrantyMonths(values.purchasedAt, months);
        if (!until || until === values.warrantyUntil) return;
        setFieldValue('warrantyUntil', until);
    };

    return (
        <fieldset disabled={disabled}>
            <ProductsSelect
                title={t('fields.name')}
                value={values.productId || null}
                required
                disabled={disabled || Boolean(row?.ID)}
                onChange={(product) => {
                    setFieldValue('productId', Number(product?.value) || 0);
                    if (!creating) return;
                    setFieldValue('actualVendorId', product?.vendorId || null);
                    if (product?.value) serialsRef.current?.focus();
                }}
            />
            <Form.Group className="mb-3">
                <Form.Label className="text-muted" htmlFor="warranty-actual-vendor">
                    {t('fields.actualVendor')}
                </Form.Label>
                <VendorSelect
                    inputId="warranty-actual-vendor"
                    required={false}
                    isClearable
                    value={values.actualVendorId}
                    disabled={disabled || !creating}
                    onChange={(vendor) => setFieldValue('actualVendorId', vendor?.value ?? null)}
                />
            </Form.Group>
            {creating ? (
                <Form.Group className="mb-3">
                    <Form.Label className="text-muted">{t('fields.serialNumber')}</Form.Label>
                    <WarrantySerialNumbers
                        ref={serialsRef}
                        value={values.serialNumbers}
                        disabled={disabled}
                        onChange={(serialNumbers) => setFieldValue('serialNumbers', serialNumbers)}
                    />
                </Form.Group>
            ) : (
                <Form.Group className="mb-3">
                    <Form.Label className="text-muted">{t('fields.serialNumber')}</Form.Label>
                    <Form.Control
                        name="serialNumber"
                        required
                        maxLength={255}
                        value={values.serialNumber}
                        onChange={handleChange}
                        disabled
                    />
                </Form.Group>
            )}
            <Row>
                <Col md={5}>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-muted" htmlFor="warranty-purchased-at">
                            {t('fields.purchasedAt')}
                        </Form.Label>
                        <AppDatePicker
                            id="warranty-purchased-at"
                            value={values.purchasedAt}
                            disabled={disabled || !creating}
                            onChange={(iso) => {
                                if (iso === values.purchasedAt) return;
                                setFieldValue('purchasedAt', iso);
                                if (iso && values.warrantyUntil && values.warrantyUntil < iso) {
                                    setFieldValue('warrantyUntil', iso);
                                }
                            }}
                        />
                    </Form.Group>
                </Col>
                <Col md={2}>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-muted" htmlFor="warranty-term-months">
                            {t('fields.termMonths')}
                        </Form.Label>
                        <WarrantyTermMonths
                            id="warranty-term-months"
                            purchasedAt={values.purchasedAt}
                            warrantyUntil={values.warrantyUntil}
                            disabled={disabled}
                            onChangeMonths={applyMonths}
                        />
                    </Form.Group>
                </Col>
                <Col md={5}>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-muted" htmlFor="warranty-until">
                            {t('fields.warrantyUntil')}
                        </Form.Label>
                        <AppDatePicker
                            id="warranty-until"
                            value={values.warrantyUntil}
                            min={values.purchasedAt || undefined}
                            disabled={disabled}
                            onChange={(iso) => {
                                if (iso === values.warrantyUntil) return;
                                setFieldValue('warrantyUntil', iso);
                            }}
                        />
                    </Form.Group>
                </Col>
            </Row>
            {!creating && (
                <ObjectSelected
                    title={t('fields.object')}
                    value={objectSelect.value}
                    getList={objectSelect.getList}
                    disabled={disabled || row?.canAssign === false}
                    clearable
                    onChange={(object) => setFieldValue('objectId', object?.ID || null)}
                />
            )}
        </fieldset>
    );
};

export default TabGeneral;
